import React, { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { EntriesPanel } from '../components/EntriesPanel';
import { PeninsulaProgressLoop } from '../components/PeninsulaProgressLoop';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { ActivityEntriesModal } from '../components/ActivityEntriesModal';
import { ChapterMemberSearchDropdown } from '../components/ChapterMemberSearchDropdown';
import { useIsMobile } from '../hooks/useIsMobile';

interface ActivityEntriesResponse {
  type: FundraisingType;
  entries: Record<string, unknown>[];
}

type FundraisingType = 'VirtualWalkRun' | 'TshirtSale' | 'Sponsorship' | 'Donation' | 'GeneralOther';

interface FundraisingActivity {
  id: string;
  name: string;
  type: FundraisingType;
  description: string;
  goalAmount: number;
  costPerMile?: number | null;
  startDate: string;
  endDate: string;
  totalRaised?: number;
}

interface FundraisingSummary {
  totalRaised: number;
  goal: number;
  activities: FundraisingActivity[];
}

interface GoalData {
  amount: number;
}

const TYPE_LABELS: Record<FundraisingType, string> = {
  VirtualWalkRun: 'Virtual Walk/Run',
  TshirtSale: 'T-Shirt Sale',
  Sponsorship: 'Sponsorship',
  Donation: 'Donation',
  GeneralOther: 'General/Other',
};

const TYPE_COLORS: Record<FundraisingType, string> = {
  VirtualWalkRun: '#1a6b3c',
  TshirtSale: '#c9a84c',
  Sponsorship: '#0f2d1f',
  Donation: '#4a90d9',
  GeneralOther: '#666',
};

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  pageMobile: { padding: '1rem', paddingBottom: 'calc(72px + env(safe-area-inset-bottom))', maxWidth: '100%' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  titleMobile: { fontSize: '1.25rem', margin: '0 0 1rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  progressContainer: { marginBottom: '1.5rem' },
  progressBar: { width: '100%', height: '28px', backgroundColor: '#e0e0e0', borderRadius: '14px', overflow: 'hidden', position: 'relative' as const },
  progressFill: { height: '100%', borderRadius: '14px', transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', fontWeight: 600 },
  goalReached: { padding: '0.75rem 1.25rem', backgroundColor: '#e8f5e9', borderRadius: '6px', color: '#1a6b3c', fontWeight: 600, fontSize: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
  activityCard: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff', marginRight: '0.5rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  trClickable: { cursor: 'pointer', transition: 'background-color 0.15s' },
  trExpanded: { backgroundColor: '#f0f7f2' },
};

export function FundraisingPage() {
  const { user } = useAuth();
  const { canEdit } = usePageAuthorization('fundraising');
  const isOrganizer = isAtLeast(user?.role || '', 'Organizer');
  const isMobile = useIsMobile();
  const [summary, setSummary] = useState<FundraisingSummary | null>(null);
  const [goal, setGoal] = useState<GoalData | null>(null);
  const [activities, setActivities] = useState<FundraisingActivity[]>([]);
  const [error, setError] = useState('');
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState<string | null>(null);

  // View mode and entries expansion state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);
  const [activityEntries, setActivityEntries] = useState<ActivityEntriesResponse | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [detailSummarized, setDetailSummarized] = useState(false);
  const [expandedParticipant, setExpandedParticipant] = useState<string | null>(null);

  // Edit activity state
  const [editingActivity, setEditingActivity] = useState<FundraisingActivity | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editGoal, setEditGoal] = useState('');
  const [editCostPerMile, setEditCostPerMile] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editError, setEditError] = useState('');

  // Create activity form
  const [actName, setActName] = useState('');
  const [actType, setActType] = useState<FundraisingType>('GeneralOther');
  const [actDesc, setActDesc] = useState('');
  const [actGoal, setActGoal] = useState('');
  const [actCostPerMile, setActCostPerMile] = useState('');
  const [actStart, setActStart] = useState('');
  const [actEnd, setActEnd] = useState('');

  // Add entry form (type-specific)
  const [entryFields, setEntryFields] = useState<Record<string, string>>({});

  // Edit entry state
  const [editingEntry, setEditingEntry] = useState<Record<string, unknown> | null>(null);
  const [editEntryFields, setEditEntryFields] = useState<Record<string, string>>({});
  const [editEntrySaving, setEditEntrySaving] = useState(false);

  // Activity Entries Modal state
  const [modalActivity, setModalActivity] = useState<FundraisingActivity | null>(null);

  // Duplicate warning for inline add form
  const [inlineDuplicateWarning, setInlineDuplicateWarning] = useState('');
  const [inlineCheckEntries, setInlineCheckEntries] = useState<Record<string, unknown>[]>([]);

  async function fetchInlineEntries(activityId: string) {
    try {
      const data = await apiClient<ActivityEntriesResponse>(`/fundraising/activities/${activityId}/entries`);
      setInlineCheckEntries(data.entries);
    } catch { setInlineCheckEntries([]); }
  }

  function checkInlineDuplicate(activityType: FundraisingType, fieldKey: string, value: string) {
    const dupeKeys: Record<string, string[]> = {
      Sponsorship: ['sponsorName'],
      GeneralOther: ['description'],
      Donation: ['donorName'],
      VirtualWalkRun: ['sponsorName'],
      TshirtSale: ['size', 'style'],
    };
    const keysToCheck = dupeKeys[activityType] || [];
    if (!keysToCheck.includes(fieldKey) || !value.trim()) {
      setInlineDuplicateWarning('');
      return;
    }
    const normalizedValue = value.trim().toLowerCase();
    const match = inlineCheckEntries.find((e) => {
      const existing = e[fieldKey];
      return typeof existing === 'string' && existing.trim().toLowerCase() === normalizedValue;
    });
    if (match) {
      const label = getEntryFieldsForType(activityType).find(f => f.key === fieldKey)?.label || fieldKey;
      setInlineDuplicateWarning(`⚠️ Duplicate: an entry with ${label} "${value.trim()}" already exists.`);
    } else {
      setInlineDuplicateWarning('');
    }
  }

  async function fetchData() {
    try {
      const [summaryData, goalData, activityList] = await Promise.all([
        apiClient<FundraisingSummary>('/fundraising/summary').catch(() => null),
        apiClient<GoalData>('/fundraising/goal').catch(() => null),
        apiClient<FundraisingActivity[]>('/fundraising/activities'),
      ]);
      setSummary(summaryData);
      setGoal(goalData);
      setActivities(activityList);
    } catch {
      setError('Failed to load fundraising data.');
    }
  }

  useEffect(() => { fetchData(); }, []);

  // Deep-link: /giving?new=1 (from the "Create Giving Campaign" intent) opens
  // the create form straight away.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') setShowCreateActivity(true);
  }, [searchParams]);

  async function handleCreateActivity(e: FormEvent) {
    e.preventDefault();
    if (!actName || !actType || !actGoal || !actStart || !actEnd) {
      setError('All fields are required.');
      return;
    }
    try {
      await apiClient('/fundraising/activities', {
        method: 'POST',
        body: { name: actName, type: actType, description: actDesc, goalAmount: parseFloat(actGoal), ...(actCostPerMile && { costPerMile: parseFloat(actCostPerMile) }), startDate: actStart, endDate: actEnd },
      });
      setActName(''); setActType('GeneralOther'); setActDesc(''); setActGoal(''); setActCostPerMile(''); setActStart(''); setActEnd('');
      setShowCreateActivity(false);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create activity.');
    }
  }

  async function handleAddEntry(e: FormEvent, activityId: string) {
    e.preventDefault();
    try {
      // Convert numeric fields from strings to numbers for API validation
      const activity = activities.find((a) => a.id === activityId);
      if (!activity) return;

      const fields = getEntryFieldsForType(activity.type);
      const numericKeys = fields
        .filter((f) => f.inputType === 'number')
        .map((f) => f.key);

      const body: Record<string, unknown> = {};
      for (const field of fields) {
        const value = entryFields[field.key] || '';
        if (field.inputType === 'member-search') {
          // UUID from dropdown — pass as-is (or skip if empty to let server validate)
          if (value) body[field.key] = value;
        } else if (numericKeys.includes(field.key) && value !== '') {
          body[field.key] = field.key === 'quantitySold' ? parseInt(value, 10) : parseFloat(value);
        } else if (value !== '') {
          body[field.key] = value;
        }
      }

      await apiClient(`/fundraising/activities/${activityId}/entries`, {
        method: 'POST',
        body,
      });
      setEntryFields({});
      setShowAddEntry(null);
      fetchData();
      // Also refresh the expanded entries panel if this activity is expanded
      if (expandedActivityId === activityId) {
        try {
          const data = await apiClient<ActivityEntriesResponse>(`/fundraising/activities/${activityId}/entries`);
          setActivityEntries(data);
        } catch {
          // Silently fail — main data already refreshed
        }
      }
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add entry.');
    }
  }

  function openEditEntry(entry: Record<string, unknown>) {
    setEditingEntry(entry);
    const fields: Record<string, string> = {};
    for (const [key, val] of Object.entries(entry)) {
      if (key === 'id') continue;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        fields[key] = val.split('T')[0];
      } else {
        fields[key] = val != null ? String(val) : '';
      }
    }
    setEditEntryFields(fields);
  }

  async function handleSaveEntry() {
    if (!editingEntry || !expandedActivityId) return;
    setEditEntrySaving(true);
    try {
      const body: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(editEntryFields)) {
        if (key === 'id' || key === 'participantName') continue;
        const original = editingEntry[key];
        if (typeof original === 'number') {
          body[key] = parseFloat(val) || 0;
        } else {
          body[key] = val;
        }
      }
      await apiClient(`/fundraising/activities/${expandedActivityId}/entries/${editingEntry.id}`, {
        method: 'PATCH',
        body,
      });
      setEditingEntry(null);
      setEditEntryFields({});
      setError('');
      // Refresh entries
      const data = await apiClient<ActivityEntriesResponse>(`/fundraising/activities/${expandedActivityId}/entries`);
      setActivityEntries(data);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update entry.');
    } finally {
      setEditEntrySaving(false);
    }
  }

  async function handleDeleteEntry(entryId: string) {
    if (!expandedActivityId) return;
    if (!confirm('Are you sure you want to delete this entry?')) return;
    try {
      await apiClient(`/fundraising/activities/${expandedActivityId}/entries/${entryId}`, {
        method: 'DELETE',
      });
      setError('');
      const data = await apiClient<ActivityEntriesResponse>(`/fundraising/activities/${expandedActivityId}/entries`);
      setActivityEntries(data);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete entry.');
    }
  }

  async function handleExportCsv() {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/fundraising/export`,
        { headers: { Authorization: `Bearer ${document.cookie}` } }
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'fundraising-report.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV.');
    }
  }

  // handleActivityClick removed — modal is now used for entry viewing

  function openEditActivity(activity: FundraisingActivity) {
    setEditingActivity(activity);
    setEditName(activity.name);
    setEditDesc(activity.description || '');
    setEditGoal(String(activity.goalAmount));
    setEditCostPerMile(activity.costPerMile ? String(activity.costPerMile) : '');
    setEditStart(activity.startDate.split('T')[0]);
    setEditEnd(activity.endDate.split('T')[0]);
    setEditError('');
  }

  async function handleSaveActivity() {
    if (!editingActivity) return;
    if (!editName || !editGoal || !editStart || !editEnd) {
      setEditError('Name, goal, start date, and end date are required.');
      return;
    }
    try {
      await apiClient(`/fundraising/activities/${editingActivity.id}`, {
        method: 'PATCH',
        body: {
          name: editName,
          description: editDesc,
          goalAmount: parseFloat(editGoal),
          ...(editCostPerMile && { costPerMile: parseFloat(editCostPerMile) }),
          startDate: editStart,
          endDate: editEnd,
        },
      });
      setEditingActivity(null);
      setEditError('');
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string | string[] };
      const msg = Array.isArray(apiErr.message) ? apiErr.message.join(', ') : (apiErr.message || 'Failed to update activity.');
      setEditError(msg);
    }
  }

  function getEntryFieldsForType(type: FundraisingType): { key: string; label: string; inputType: string }[] {
    switch (type) {
      case 'VirtualWalkRun':
        return [
          { key: 'participantName', label: 'Participant Name', inputType: 'member-search' },
          { key: 'sponsorName', label: 'Sponsor Name', inputType: 'text' },
          { key: 'milesSponsored', label: 'Miles Sponsored', inputType: 'number' },
          { key: 'sponsoredDate', label: 'Sponsored Date', inputType: 'date' },
          { key: 'entryDate', label: 'Entry Date', inputType: 'date' },
        ];
      case 'TshirtSale':
        return [
          { key: 'size', label: 'Size', inputType: 'text' },
          { key: 'style', label: 'Style', inputType: 'text' },
          { key: 'quantitySold', label: 'Quantity Sold', inputType: 'number' },
          { key: 'pricePerItem', label: 'Price Per Item', inputType: 'number' },
        ];
      case 'Sponsorship':
        return [
          { key: 'sponsorName', label: 'Sponsor Name', inputType: 'text' },
          { key: 'tier', label: 'Tier', inputType: 'text' },
          { key: 'pledgedAmount', label: 'Pledged Amount', inputType: 'number' },
          { key: 'receivedAmount', label: 'Received Amount', inputType: 'number' },
        ];
      case 'Donation':
        return [
          { key: 'donorName', label: 'Donor Name (optional)', inputType: 'text' },
          { key: 'donationAmount', label: 'Donation Amount', inputType: 'number' },
          { key: 'donationDate', label: 'Donation Date', inputType: 'date' },
        ];
      case 'GeneralOther':
        return [
          { key: 'description', label: 'Description', inputType: 'text' },
          { key: 'amount', label: 'Amount', inputType: 'number' },
        ];
    }
  }

  const totalRaised = activities.reduce((sum, a) => sum + (a.totalRaised ?? 0), 0);
  const totalActivityGoals = activities.reduce((sum, a) => sum + Number(a.goalAmount), 0);
  const goalAmount = totalActivityGoals > 0 ? totalActivityGoals : (goal?.amount ?? summary?.goal ?? 0);
  const progressPct = goalAmount > 0 ? Math.min((totalRaised / goalAmount) * 100, 100) : 0;
  const goalReached = goalAmount > 0 && totalRaised >= goalAmount;

  const formGridStyle: React.CSSProperties = isMobile
    ? { ...styles.formGrid, gridTemplateColumns: '1fr' }
    : styles.formGrid;

  return (
    <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : null) }}>
      <h1 style={{ ...styles.title, ...(isMobile ? styles.titleMobile : null) }}>Fundraising</h1>
      {error && <div style={styles.error}>{error}</div>}

      {/* Goal Reached Indicator */}
      {goalReached && (
        <div style={styles.goalReached}>
          <span style={{ fontSize: '1.5rem' }}>🎉</span>
          Goal Reached! ${totalRaised.toLocaleString('en-US', { minimumFractionDigits: 2 })} raised of ${goalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} goal
        </div>
      )}

      {/* Goal Progress */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Goal Progress</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 600, color: '#0f2d1f' }}>
            ${totalRaised.toLocaleString('en-US', { minimumFractionDigits: 2 })} raised
          </span>
          <span style={{ color: '#666' }}>
            Target: ${goalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div style={styles.progressBar}>
          <div style={{ display: 'flex', height: '100%', width: `${progressPct}%`, borderRadius: '14px', overflow: 'hidden' }}>
            {activities
              .filter((a) => (a.totalRaised ?? 0) > 0)
              .map((a) => {
                return (
                  <div
                    key={a.id}
                    title={`${a.name}: $${(a.totalRaised ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                    style={{
                      width: `${totalRaised > 0 ? ((a.totalRaised ?? 0) / totalRaised) * 100 : 0}%`,
                      height: '100%',
                      backgroundColor: TYPE_COLORS[a.type],
                      transition: 'width 0.3s',
                    }}
                  />
                );
              })}
          </div>
          {progressPct > 8 && (
            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', fontWeight: 600, pointerEvents: 'none' }}>
              {progressPct.toFixed(1)}%
            </span>
          )}
        </div>
        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' }}>
          {activities
            .filter((a) => (a.totalRaised ?? 0) > 0)
            .map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: TYPE_COLORS[a.type], display: 'inline-block' }} />
                <span style={{ color: '#333' }}>{a.name}</span>
                <span style={{ color: '#666' }}>(${(a.totalRaised ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
              </div>
            ))}
        </div>
      </div>

      {/* Activities */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Activities</h2>
            <button
              style={viewMode === 'cards' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
          <div>
            {canEdit && <button style={styles.btn} onClick={() => setShowCreateActivity(!showCreateActivity)}>+ New Activity</button>}
            <button style={styles.btnGold} onClick={handleExportCsv}>Export CSV</button>
          </div>
        </div>

        {/* Create Activity Form */}
        {showCreateActivity && (
          <form onSubmit={handleCreateActivity} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem' }}>
            <div style={formGridStyle}>
              <div style={styles.field}>
                <label style={styles.label}>Name</label>
                <input style={{ ...styles.input, width: '100%' }} value={actName} onChange={(e) => setActName(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Type</label>
                <select style={{ ...styles.select, width: '100%' }} value={actType} onChange={(e) => setActType(e.target.value as FundraisingType)}>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Description</label>
                <input style={{ ...styles.input, width: '100%' }} value={actDesc} onChange={(e) => setActDesc(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Goal Amount</label>
                <input type="number" step="0.01" min="0.01" style={{ ...styles.input, width: '100%' }} value={actGoal} onChange={(e) => setActGoal(e.target.value)} />
              </div>
              {actType === 'VirtualWalkRun' && (
              <div style={styles.field}>
                <label style={styles.label}>Cost Per Mile ($)</label>
                <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '100%' }} value={actCostPerMile} onChange={(e) => setActCostPerMile(e.target.value)} placeholder="e.g. 15" />
              </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>Start Date</label>
                <input type="date" style={{ ...styles.input, width: '100%' }} value={actStart} onChange={(e) => setActStart(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>End Date</label>
                <input type="date" style={{ ...styles.input, width: '100%' }} value={actEnd} onChange={(e) => setActEnd(e.target.value)} />
              </div>
            </div>
            <button type="submit" style={styles.btn}>Create Activity</button>
          </form>
        )}

        {/* Activity List */}
        {activities.length === 0 ? (
          <p style={{ color: '#666' }}>No fundraising activities yet.</p>
        ) : viewMode === 'cards' ? (
          /* Card View */
          activities.map((activity) => (
            <div key={activity.id} style={styles.activityCard}>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => setModalActivity(activity)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ ...styles.badge, backgroundColor: TYPE_COLORS[activity.type] }}>{TYPE_LABELS[activity.type]}</span>
                    <strong style={{ color: '#0f2d1f' }}>{activity.name}</strong>
                  </div>
                  <div style={{ textAlign: 'right' as const }}>
                    <div style={{ fontWeight: 700, color: '#1a6b3c' }}>
                      ${(activity.totalRaised ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      of ${Number(activity.goalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })} goal
                    </div>
                  </div>
                </div>
                {activity.description && <p style={{ fontSize: '0.85rem', color: '#444', margin: '0.5rem 0' }}>{activity.description}</p>}
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  {new Date(activity.startDate).toLocaleDateString()} – {new Date(activity.endDate).toLocaleDateString()}
                </div>
              </div>
              {canEdit && (
              <button
                style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                onClick={() => {
                  const isOpen = showAddEntry === activity.id;
                  setShowAddEntry(isOpen ? null : activity.id);
                  if (!isOpen) {
                    const today = new Date().toISOString().split('T')[0];
                    const defaults: Record<string, string> = {};
                    getEntryFieldsForType(activity.type).forEach((f) => { if (f.inputType === 'date') defaults[f.key] = today; });
                    setEntryFields(defaults);
                    fetchInlineEntries(activity.id);
                    setInlineDuplicateWarning('');
                  } else { setEntryFields({}); setInlineDuplicateWarning(''); }
                }}
              >
                + Add Entry
              </button>
              )}
              {canEdit && isOrganizer && (
                <button
                  style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem', backgroundColor: '#c9a84c' }}
                  onClick={() => openEditActivity(activity)}
                >
                  Edit
                </button>
              )}

              {/* Add Entry Form */}
              {showAddEntry === activity.id && (
                <form onSubmit={(e) => handleAddEntry(e, activity.id)} style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    {getEntryFieldsForType(activity.type).map((field) => (
                      <div key={field.key}>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>{field.label}</label>
                        {field.inputType === 'member-search' ? (
                          <div style={{ width: '200px' }}>
                            <ChapterMemberSearchDropdown
                              value={entryFields[field.key] || null}
                              onChange={(_memberId, fullName) => {
                                setEntryFields({ ...entryFields, [field.key]: fullName });
                                checkInlineDuplicate(activity.type, field.key, fullName);
                              }}
                              placeholder="Search GA chapter members..."
                            />
                          </div>
                        ) : (
                          <input
                            type={field.inputType}
                            step={field.inputType === 'number' ? '0.01' : undefined}
                            style={{ ...styles.input, width: '140px' }}
                            value={entryFields[field.key] || ''}
                            onChange={(e) => {
                              setEntryFields({ ...entryFields, [field.key]: e.target.value });
                              checkInlineDuplicate(activity.type, field.key, e.target.value);
                            }}
                          />
                        )}
                      </div>
                    ))}
                    <button type="submit" style={{ ...styles.btn, marginBottom: '0' }}>Add</button>
                  </div>
                  {inlineDuplicateWarning && (
                    <div style={{ padding: '6px 10px', backgroundColor: '#fffbea', border: '1px solid #f59e0b', borderRadius: '4px', marginTop: '0.5rem', fontSize: '0.78rem', color: '#92400e' }}>
                      {inlineDuplicateWarning}
                    </div>
                  )}
                </form>
              )}

              {/* Entries Panel (expanded) */}
              {expandedActivityId === activity.id && (
                <EntriesPanel
                  type={activity.type}
                  entries={activityEntries?.entries ?? []}
                  loading={entriesLoading}
                />
              )}
            </div>
          ))
        ) : (
          /* Table View */
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Raised</th>
                  <th style={styles.th}>Goal</th>
                  <th style={styles.th}>Start Date</th>
                  <th style={styles.th}>End Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => (
                  <React.Fragment key={activity.id}>
                    <tr
                      style={{
                        ...styles.trClickable,
                        ...(expandedActivityId === activity.id ? styles.trExpanded : {}),
                      }}
                      onClick={() => setModalActivity(activity)}
                    >
                      <td style={styles.td}>
                        <strong style={{ color: '#0f2d1f' }}>{activity.name}</strong>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: TYPE_COLORS[activity.type] }}>
                          {TYPE_LABELS[activity.type]}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 700, color: '#1a6b3c' }}>
                          ${(activity.totalRaised ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td style={styles.td}>
                        ${Number(activity.goalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={styles.td}>{new Date(activity.startDate).toLocaleDateString()}</td>
                      <td style={styles.td}>{new Date(activity.endDate).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        {canEdit && (
                        <button
                          style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const isOpen = showAddEntry === activity.id;
                            setShowAddEntry(isOpen ? null : activity.id);
                            if (!isOpen) {
                              const today = new Date().toISOString().split('T')[0];
                              const defaults: Record<string, string> = {};
                              getEntryFieldsForType(activity.type).forEach((f) => { if (f.inputType === 'date') defaults[f.key] = today; });
                              setEntryFields(defaults);
                            } else { setEntryFields({}); }
                          }}
                        >
                          + Add Entry
                        </button>
                        )}
                        {canEdit && isOrganizer && (
                          <button
                            style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem', backgroundColor: '#c9a84c' }}
                            onClick={(e) => { e.stopPropagation(); openEditActivity(activity); }}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Expanded entries row */}
                    {expandedActivityId === activity.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 0.5rem 0.75rem 0.5rem' }}>
                          <EntriesPanel
                            type={activity.type}
                            entries={activityEntries?.entries ?? []}
                            loading={entriesLoading}
                          />
                        </td>
                      </tr>
                    )}
                    {/* Add Entry Form row (table view) */}
                    {showAddEntry === activity.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0.5rem' }}>
                          <form onSubmit={(e) => handleAddEntry(e, activity.id)} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              {getEntryFieldsForType(activity.type).map((field) => (
                                <div key={field.key}>
                                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>{field.label}</label>
                                  {field.inputType === 'member-search' ? (
                                    <div style={{ width: '200px' }}>
                                      <ChapterMemberSearchDropdown
                                        value={entryFields[field.key] || null}
                                        onChange={(_memberId, fullName) => setEntryFields({ ...entryFields, [field.key]: fullName })}
                                        placeholder="Search GA chapter members..."
                                      />
                                    </div>
                                  ) : (
                                    <input
                                      type={field.inputType}
                                      step={field.inputType === 'number' ? '0.01' : undefined}
                                      style={{ ...styles.input, width: '140px' }}
                                      value={entryFields[field.key] || ''}
                                      onChange={(e) => setEntryFields({ ...entryFields, [field.key]: e.target.value })}
                                    />
                                  )}
                                </div>
                              ))}
                              <button type="submit" style={{ ...styles.btn, marginBottom: '0' }}>Add</button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity Detail View */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Activity Detail</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select
            style={{ ...styles.select, minWidth: '250px' }}
            value={expandedActivityId || ''}
            onChange={async (e) => {
              const id = e.target.value;
              if (!id) {
                setExpandedActivityId(null);
                setActivityEntries(null);
                return;
              }
              setExpandedActivityId(id);
              setEntriesLoading(true);
              try {
                const response = await apiClient<ActivityEntriesResponse>(`/fundraising/activities/${id}/entries`);
                setActivityEntries(response);
              } catch {
                setActivityEntries(null);
              } finally {
                setEntriesLoading(false);
              }
            }}
          >
            <option value="">Select an activity...</option>
            {activities.map((a) => (
              <option key={a.id} value={a.id}>{a.name} ({TYPE_LABELS[a.type]})</option>
            ))}
          </select>
          {expandedActivityId && activityEntries && activityEntries.entries.length > 0 && (
            <>
              <button
                style={detailSummarized ? { ...styles.btn, backgroundColor: '#c9a84c' } : { ...styles.btn, backgroundColor: '#666' }}
                onClick={() => setDetailSummarized(!detailSummarized)}
              >
                {detailSummarized ? '📋 All Entries' : '👤 Summarize by Participant'}
              </button>
              <button
                style={styles.btn}
                onClick={() => {
                  const printContent = document.getElementById('activity-detail-print');
                  if (!printContent) return;
                  const win = window.open('', '_blank');
                  if (!win) return;
                  win.document.write('<html><head><title>Activity Detail</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f4f4f4;font-weight:600}h2{color:#0f2d1f}</style></head><body>');
                  win.document.write(printContent.innerHTML);
                  win.document.write('</body></html>');
                  win.document.close();
                  win.print();
                }}
              >
                🖨️ Print
              </button>
              <button
                style={styles.btnGold}
                onClick={() => {
                  if (!activityEntries || activityEntries.entries.length === 0) return;
                  const entries = activityEntries.entries;
                  const headers = Object.keys(entries[0]);
                  const csvRows = [headers.join(',')];
                  for (const entry of entries) {
                    const row = headers.map((h) => {
                      const val = String(entry[h] ?? '').replace(/"/g, '""');
                      return `"${val}"`;
                    });
                    csvRows.push(row.join(','));
                  }
                  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const actName = activities.find((act) => act.id === expandedActivityId)?.name || 'activity';
                  a.href = url;
                  a.download = `${actName.replace(/\s+/g, '-').toLowerCase()}-entries.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                ⬇️ Download CSV
              </button>
            </>
          )}
        </div>

        {expandedActivityId && (
          <div id="activity-detail-print">
            {entriesLoading ? (
              <p style={{ color: '#666' }}>Loading entries...</p>
            ) : !activityEntries || activityEntries.entries.length === 0 ? (
              <p style={{ color: '#666' }}>No entries found for this activity.</p>
            ) : (
              <>
                <h3 style={{ color: '#0f2d1f', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
                  {activities.find((a) => a.id === expandedActivityId)?.name} — {detailSummarized ? 'Summary by Participant' : `${activityEntries.entries.length} entries`}
                </h3>

                {detailSummarized ? (
                  /* Summarized by Participant View */
                  (() => {
                    // Find the participant/name column key
                    const nameKey = Object.keys(activityEntries.entries[0]).find((k) =>
                      k.toLowerCase().includes('participant') || k.toLowerCase() === 'name'
                    ) || Object.keys(activityEntries.entries[0]).find((k) =>
                      typeof activityEntries.entries[0][k] === 'string' && !k.toLowerCase().includes('date') && !k.toLowerCase().includes('id') && !k.toLowerCase().includes('sponsor')
                    ) || '';

                    // Find summable numeric columns
                    const numericKeys = Object.keys(activityEntries.entries[0]).filter((k) => {
                      if (k === 'id') return false;
                      return activityEntries.entries.every((e) => typeof e[k] === 'number' || e[k] === null || e[k] === undefined);
                    }).filter((k) =>
                      k.toLowerCase().includes('miles') ||
                      k.toLowerCase().includes('revenue') ||
                      k.toLowerCase().includes('amount') ||
                      k.toLowerCase().includes('price') ||
                      k.toLowerCase().includes('quantity') ||
                      k.toLowerCase().includes('sponsored')
                    );

                    // Group by participant name
                    const grouped = new Map<string, { count: number; totals: Record<string, number> }>();
                    for (const entry of activityEntries.entries) {
                      const name = String(entry[nameKey] ?? 'Unknown');
                      if (!grouped.has(name)) {
                        grouped.set(name, { count: 0, totals: {} });
                        for (const nk of numericKeys) grouped.get(name)!.totals[nk] = 0;
                      }
                      const group = grouped.get(name)!;
                      group.count++;
                      for (const nk of numericKeys) {
                        group.totals[nk] += Number(entry[nk]) || 0;
                      }
                    }

                    const sortedParticipants = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));

                    return (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem' }}>Participant</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem' }}>Entries</th>
                              {numericKeys.map((nk) => (
                                <th key={nk} style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                  {nk.replace(/([A-Z])/g, ' $1').trim()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedParticipants.map(([name, data]) => (
                              <React.Fragment key={name}>
                              <tr
                                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: expandedParticipant === name ? '#f9f9f9' : undefined }}
                                onClick={() => setExpandedParticipant(expandedParticipant === name ? null : name)}
                              >
                                <td style={{ padding: '0.5rem', color: '#333', fontWeight: 500 }}>
                                  {expandedParticipant === name ? '▼' : '▶'} {name}
                                </td>
                                <td style={{ padding: '0.5rem', color: '#333' }}>{data.count}</td>
                                {numericKeys.map((nk) => {
                                  const isCurrency = nk.toLowerCase().includes('revenue') || nk.toLowerCase().includes('amount') || nk.toLowerCase().includes('price');
                                  return (
                                    <td key={nk} style={{ padding: '0.5rem', color: '#333' }}>
                                      {isCurrency ? `$${data.totals[nk].toLocaleString('en-US', { minimumFractionDigits: 2 })}` : data.totals[nk].toLocaleString()}
                                    </td>
                                  );
                                })}
                              </tr>
                              {expandedParticipant === name && (
                                <tr>
                                  <td colSpan={2 + numericKeys.length} style={{ padding: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
                                    <PeninsulaProgressLoop
                                      participantName={name}
                                      participantId={(() => {
                                        // Find the first entry for this participant to get its id
                                        const entry = activityEntries.entries.find((e) => String(e[nameKey]) === name);
                                        return String(entry?.id || '');
                                      })()}
                                      milesSponsored={data.totals[numericKeys.find((k) => k.toLowerCase().includes('miles') || k.toLowerCase().includes('sponsored')) || ''] || 0}
                                      milesAchieved={0}
                                    />
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ borderTop: '2px solid #0f2d1f', fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                              <td style={{ padding: '0.5rem', color: '#0f2d1f' }}>TOTALS</td>
                              <td style={{ padding: '0.5rem', color: '#0f2d1f' }}>{activityEntries.entries.length}</td>
                              {numericKeys.map((nk) => {
                                const total = activityEntries.entries.reduce((sum, e) => sum + (Number(e[nk]) || 0), 0);
                                const isCurrency = nk.toLowerCase().includes('revenue') || nk.toLowerCase().includes('amount') || nk.toLowerCase().includes('price');
                                return (
                                  <td key={nk} style={{ padding: '0.5rem', color: '#0f2d1f' }}>
                                    {isCurrency ? `$${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : total.toLocaleString()}
                                  </td>
                                );
                              })}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  /* Full Entries Table */
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        {Object.keys(activityEntries.entries[0]).filter((k) => k !== 'id').map((key) => (
                          <th key={key} style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </th>
                        ))}
                        {canEdit && <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem' }}>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {activityEntries.entries.map((entry, idx) => (
                        <tr key={String(entry.id || idx)} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          {Object.entries(entry).filter(([k]) => k !== 'id').map(([key, val]) => (
                            <td key={key} style={{ padding: '0.5rem', color: '#333' }}>
                              {typeof val === 'number'
                                ? key.toLowerCase().includes('amount') || key.toLowerCase().includes('revenue') || key.toLowerCase().includes('price')
                                  ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                                  : val.toLocaleString()
                                : val instanceof Date || (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val))
                                  ? new Date(val as string).toLocaleDateString()
                                  : String(val ?? '—')}
                            </td>
                          ))}
                          {canEdit && (
                            <td style={{ padding: '0.5rem' }}>
                              <button
                                style={{ padding: '0.2rem 0.5rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.25rem' }}
                                onClick={() => openEditEntry(entry)}
                              >Edit</button>
                              <button
                                style={{ padding: '0.2rem 0.5rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}
                                onClick={() => handleDeleteEntry(String(entry.id))}
                              >Del</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #0f2d1f', fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                        {Object.keys(activityEntries.entries[0]).filter((k) => k !== 'id').map((key, colIdx) => {
                          const isNumericCol = activityEntries.entries.every((e) => typeof e[key] === 'number' || e[key] === null || e[key] === undefined);
                          const isSummable = isNumericCol && (
                            key.toLowerCase().includes('miles') ||
                            key.toLowerCase().includes('revenue') ||
                            key.toLowerCase().includes('amount') ||
                            key.toLowerCase().includes('price') ||
                            key.toLowerCase().includes('quantity') ||
                            key.toLowerCase().includes('sponsored')
                          );
                          if (colIdx === 0 && !isSummable) {
                            return <td key={key} style={{ padding: '0.5rem', color: '#0f2d1f' }}>TOTALS</td>;
                          }
                          if (isSummable) {
                            const total = activityEntries.entries.reduce((sum, e) => sum + (Number(e[key]) || 0), 0);
                            const isCurrency = key.toLowerCase().includes('revenue') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('price');
                            return (
                              <td key={key} style={{ padding: '0.5rem', color: '#0f2d1f' }}>
                                {isCurrency ? `$${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : total.toLocaleString()}
                              </td>
                            );
                          }
                          return <td key={key} style={{ padding: '0.5rem' }}>{colIdx === 0 ? 'TOTALS' : ''}</td>;
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Edit Activity Modal */}
      {editingActivity && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '2rem', width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <h2 style={{ color: '#0f2d1f', fontSize: '1.2rem', marginTop: 0, marginBottom: '1rem' }}>Edit Activity</h2>
            {editError && <div style={styles.error}>{editError}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input style={{ ...styles.input, width: '100%' }} value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <input style={{ ...styles.input, width: '100%' }} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
            </div>
            <div style={formGridStyle}>
              <div style={styles.field}>
                <label style={styles.label}>Goal Amount</label>
                <input type="number" step="0.01" min="0.01" style={{ ...styles.input, width: '100%' }} value={editGoal} onChange={(e) => setEditGoal(e.target.value)} />
              </div>
              {editingActivity?.type === 'VirtualWalkRun' && (
              <div style={styles.field}>
                <label style={styles.label}>Cost Per Mile ($)</label>
                <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '100%' }} value={editCostPerMile} onChange={(e) => setEditCostPerMile(e.target.value)} placeholder="e.g. 15" />
              </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>Type</label>
                <input style={{ ...styles.input, width: '100%', backgroundColor: '#f5f5f5' }} value={TYPE_LABELS[editingActivity.type]} disabled />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Start Date</label>
                <input type="date" style={{ ...styles.input, width: '100%' }} value={editStart} onChange={(e) => setEditStart(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>End Date</label>
                <input type="date" style={{ ...styles.input, width: '100%' }} value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={styles.btn} onClick={handleSaveActivity}>Save</button>
              {editingActivity?.type === 'VirtualWalkRun' && (
                <button style={{ ...styles.btn, backgroundColor: '#c9a84c' }} onClick={async () => {
                  try {
                    const result = await apiClient<{ updatedCount: number; costPerMile: number }>(`/fundraising/activities/${editingActivity.id}/recalculate`, { method: 'POST' });
                    setEditError('');
                    alert(`Recalculated ${result.updatedCount} entries at $${result.costPerMile}/mile.`);
                    fetchData();
                  } catch (err: unknown) {
                    const apiErr = err as { message?: string };
                    setEditError(apiErr.message || 'Failed to recalculate.');
                  }
                }}>Recalculate Revenue</button>
              )}
              <button style={{ ...styles.btn, backgroundColor: '#666' }} onClick={() => { setEditingActivity(null); setEditError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '2rem', width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <h2 style={{ color: '#0f2d1f', fontSize: '1.2rem', marginTop: 0, marginBottom: '1rem' }}>Edit Entry</h2>
            {error && <div style={styles.error}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {Object.entries(editEntryFields).filter(([k]) => k !== 'id' && k !== 'participantName').map(([key, val]) => (
                <div key={key} style={styles.field}>
                  <label style={styles.label}>{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                  <input
                    type={typeof editingEntry[key] === 'number' ? 'number' : key.toLowerCase().includes('date') ? 'date' : 'text'}
                    step={typeof editingEntry[key] === 'number' ? '0.01' : undefined}
                    style={{ ...styles.input, width: '100%' }}
                    value={val}
                    onChange={(e) => setEditEntryFields({ ...editEntryFields, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={styles.btn} onClick={handleSaveEntry} disabled={editEntrySaving}>
                {editEntrySaving ? 'Saving...' : 'Save'}
              </button>
              <button style={{ ...styles.btn, backgroundColor: '#666' }} onClick={() => { setEditingEntry(null); setEditEntryFields({}); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Entries Modal */}
      {modalActivity && (
        <ActivityEntriesModal
          activity={modalActivity}
          onClose={() => setModalActivity(null)}
          onDataChanged={fetchData}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
